import {useMutation} from '@apollo/client';
import {Button, Input, Select, Spin, Switch, Upload} from 'antd';
import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import DBModal from 'components/modal/modal';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import imgUpload from 'images/upload_image.png';
import {
  resizeImageFile,
  UploadDocumentToBucket,
} from 'pages/campaigns/new/screens/legalDocument/uploadDocument';
import styles from './createWalletCurrency.module.scss';
import {
  CONFIG_LANGUAGES,
  LOCALIZED_FIELDS,
  emptyConfigForm,
} from './paymentMethodConfig';

/**
 * The wallet's existing logo bucket, shared with the mobile money operator
 * flags.
 *
 * A dedicated `payment-method-logos` bucket would read better, and the presign
 * mutation happily signs a URL for one — but presigning does not check that
 * the bucket exists, so the upload itself then fails CORS at S3. Creating and
 * configuring the bucket is an infrastructure change; until it exists, these
 * logos live alongside the operator flags, which is at least the same kind of
 * asset in the same part of the product.
 */
const LOGO_BUCKET = 'momo-operators-logo';

/**
 * The logo for one method.
 *
 * An upload rather than a URL box — every other image in the wallet goes to a
 * bucket, and asking an operator to paste a hosted URL both breaks that
 * pattern and invites a link to somewhere that later disappears.
 *
 * Built compactly rather than with the shared <UploadImage>: that component's
 * smallest preview is 530x420 and its `icon` variant has no matching style at
 * all, so the image rendered unframed beside a full-width delete button. The
 * upload itself is the same `resizeImageFile` + `UploadDocumentToBucket` pair
 * the mobile money operator logo uses.
 */
const MethodLogoField = ({value, onChange, onUploadingChange}) => {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const handleFile = async file => {
    if (!file) return;
    setUploading(true);
    onUploadingChange(true);
    try {
      const resized = await resizeImageFile(file);
      const url = await UploadDocumentToBucket(
        resized,
        setProgress,
        LOGO_BUCKET,
      );
      onChange(url);
    } catch (error) {
      toast.error('Failed to upload the logo. Please try again.');
    } finally {
      setUploading(false);
      onUploadingChange(false);
      setProgress(0);
    }
  };

  if (value) {
    return (
      <div className={styles.logoPreview}>
        <img src={value} alt="" />
        <button
          type="button"
          className={styles.configWhy}
          onClick={() => onChange('')}>
          Remove logo
        </button>
      </div>
    );
  }

  return (
    <Upload
      accept=".png, .jpeg, .jpg, .svg"
      name="file"
      showUploadList={false}
      beforeUpload={file => {
        handleFile(file);
        // Uploading is handled here, not by antd.
        return false;
      }}>
      <div className={styles.logoDrop}>
        {uploading ? (
          <div className={styles.logoUploading}>
            <Spin size="small" />
            <span>{progress}%</span>
          </div>
        ) : (
          <>
            <img src={imgUpload} alt="" />
            <span>Upload logo</span>
          </>
        )}
      </div>
    </Upload>
  );
};

MethodLogoField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onUploadingChange: PropTypes.func,
};

/**
 * One method's configuration, for one direction.
 *
 * Edits are staged in a local draft and only handed back on save, so closing
 * the modal leaves the currency exactly as it was. Editing in place — as the
 * accordion did — meant there was no way to back out of a change once typed.
 */
const PaymentMethodConfigModal = ({
  isOpen,
  onClose,
  onSave,
  method,
  methodName,
  directionLabel,
  config,
  countries = [],
}) => {
  const [draft, setDraft] = useState(emptyConfigForm());
  const [uploading, setUploading] = useState(false);
  // Which field is mid-translation, so only that row's button spins.
  const [translating, setTranslating] = useState(null);

  const [translateText] = useMutation(TRANSLATE_TEXT, {errorPolicy: 'all'});

  // Reseed whenever a different method is opened, so the previous row's
  // wording never bleeds into the next one.
  useEffect(() => {
    if (isOpen) {
      setDraft({...emptyConfigForm(), ...(config || {})});
      setUploading(false);
    }
  }, [isOpen, method]);

  const patch = changes => setDraft(current => ({...current, ...changes}));

  const patchLocalized = (field, language, text) =>
    setDraft(current => ({
      ...current,
      [field]: {...(current[field] || {}), [language]: text},
    }));

  const handleTranslate = async field => {
    const sourceText = String(draft[field.key]?.EN ?? '').trim();
    if (!sourceText) {
      return toast.error(
        `Enter the ${field.label.toLowerCase()} in English first.`,
      );
    }

    setTranslating(field.key);
    try {
      const {data} = await translateText({
        variables: {sourceLang: 'EN', targetLang: 'FR', sourceText},
      });
      const result = data?.translateText;
      if (!result?.targetText) {
        return toast.error(result?.message || 'Translation failed');
      }
      patchLocalized(field.key, 'FR', result.targetText);
    } catch (error) {
      toast.error('Translation failed');
    } finally {
      setTranslating(null);
    }
  };

  const handleSave = () => {
    if (uploading) {
      return toast.error('Wait for the logo upload to finish before saving.');
    }
    onSave(draft);
  };

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={onClose}
      width={720}
      content={
        <div className={styles.methodModal}>
          <div className={styles.confirmHeader}>
            <h1 className={styles.confirmTitle}>{methodName}</h1>
            <p className={styles.confirmSubtitle}>
              How this method appears for {directionLabel.toLowerCase()}s. Leave
              a field blank to keep the app’s own wording.
            </p>
          </div>

          <div className={styles.methodModalBody}>
            <div className={styles.configTopRow}>
              <div className={styles.configToggle}>
                <span className={styles.configLabel}>Show this method</span>
                <span className={styles.configHelp}>
                  Off hides it but keeps this setup for later.
                </span>
                <Switch
                  className={styles.switch}
                  checked={draft.enabled !== false}
                  onChange={enabled => patch({enabled})}
                />
              </div>
              <div className={styles.configToggle}>
                <span className={styles.configLabel}>Available</span>
                <span className={styles.configHelp}>
                  Off keeps it on screen, greyed out, and refuses payments
                  through it — for an outage or a pause, where hiding it would
                  just look like it disappeared.
                </span>
                <Switch
                  className={styles.switch}
                  checked={draft.available !== false}
                  onChange={available => patch({available})}
                />
              </div>
              {/* Display order is deliberately not here. It is the row's
                position in the list behind this modal, set by dragging — a
                number box let the same position be typed on two rows. */}
            </div>

            {LOCALIZED_FIELDS.map(field => (
              <div key={field.key} className={styles.configBlock}>
                <span className={styles.configLabel}>
                  {field.label}
                  <Button
                    htmlType="button"
                    size="small"
                    loading={translating === field.key}
                    disabled={Boolean(translating)}
                    onClick={() => handleTranslate(field)}>
                    Translate
                  </Button>
                </span>
                <span className={styles.configHelp}>{field.help}</span>
                <div className={styles.configLangGrid}>
                  {CONFIG_LANGUAGES.map(language => (
                    <label key={language.key} className={styles.configLang}>
                      <span className={styles.configSubLabel}>
                        {language.label}
                      </span>
                      <Input
                        value={draft[field.key]?.[language.key] ?? ''}
                        placeholder={`${field.label} in ${language.label}`}
                        onChange={event =>
                          patchLocalized(
                            field.key,
                            language.key,
                            event.target.value,
                          )
                        }
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className={styles.configBottomRow}>
              <div className={styles.configBlock}>
                <span className={styles.configLabel}>Logo</span>
                <span className={styles.configHelp}>
                  Optional. Mobile ignores it for now, but it is stored.
                </span>
                <MethodLogoField
                  value={draft.logoUrl}
                  onChange={logoUrl => patch({logoUrl})}
                  onUploadingChange={setUploading}
                />
              </div>

              <div className={styles.configBlock}>
                <span className={styles.configLabel}>
                  Countries that cannot use this method
                </span>
                <span className={styles.configHelp}>
                  An exclusion list — everywhere except these. There is no
                  allowlist.
                </span>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  className={styles.fullWidth}
                  optionFilterProp="label"
                  placeholder="Everywhere is allowed"
                  value={draft.restrictedCountries}
                  options={countries.map(country => ({
                    value: country.countryCode,
                    label: `${country.name} (${country.countryCode})`,
                  }))}
                  onChange={restrictedCountries => patch({restrictedCountries})}
                />
                {(draft.restrictedCountries || []).length > 0 && (
                  <span className={styles.configWarning}>
                    Restrictions only apply to users with a known country —
                    about half of accounts have none and will still see this
                    method. Admin accounts are exempt, so test with a non-admin
                    user.
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.confirmFooter}>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              className={styles.primaryButton}
              onClick={handleSave}>
              Done
            </Button>
          </div>
        </div>
      }
    />
  );
};

PaymentMethodConfigModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  method: PropTypes.string,
  methodName: PropTypes.string,
  directionLabel: PropTypes.string,
  config: PropTypes.object,
  countries: PropTypes.array,
};

export default PaymentMethodConfigModal;
